import React from "react";
import { Input, Button, Tooltip } from "@heroui/react";
import { FilterSelect } from "../../../../components/ui";
import { useTranslations } from "../../../../contexts/translation.context";
import { OnlyFansFilters } from "../index";
import { ResetFiltersIc } from "../../../../components/icons";

interface FiltersSectionProps {
  filters: OnlyFansFilters;
  localSearch: string;
  hasActiveFilters: boolean;
  onSearchChange: (value: string) => void;
  onFilterChange: (key: keyof OnlyFansFilters, value: string) => void;
  onResetFilters: () => void;
}

/**
 * FiltersSection Component
 *
 * Renders the filters section with:
 * - Search input by account name/username
 * - Status filter dropdown
 * - Reset filters button (when filters are active)
 *
 * Based on Figma design and onlyu_front patterns
 */
const FiltersSection: React.FC<FiltersSectionProps> = ({
  filters,
  localSearch,
  hasActiveFilters,
  onSearchChange,
  onFilterChange,
  onResetFilters,
}) => {
  const { t } = useTranslations();

  // Owner type options
  const ownerTypeOptions = [
    { key: 'all', label: t('admin.onlyfans.filters.owner.all') },
    { key: 'creator', label: t('admin.onlyfans.filters.owner.creator') },
    { key: 'agency', label: t('admin.onlyfans.filters.owner.agency') },
  ];

  // Status options
  const statusOptions = [
    { key: 'all', label: t('admin.onlyfans.filters.status.all') },
    { key: 'active', label: t('admin.onlyfans.filters.status.active') },
    { key: 'moderation', label: t('admin.onlyfans.filters.status.moderation') },
    { key: 'wait_media', label: t('admin.onlyfans.filters.status.waitMedia') },
    { key: 'deactivated', label: t('admin.onlyfans.filters.status.deactivated') },
  ];

  return (
    <div className="px-[24px] pb-[24px] flex items-center gap-[16px] flex-wrap justify-between w-full">
      <div className="flex items-center gap-[16px] flex-wrap justify-start">
        {/* Search Input */}
        <Input
          type="text"
          placeholder={t('admin.onlyfans.filters.search.placeholder')}
          size="sm"
          value={localSearch}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onSearchChange(e.target.value)}
          endContent={
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z" stroke="#71717A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M17.5 17.5L13.875 13.875" stroke="#71717A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          }
          classNames={{
            mainWrapper: "w-[273px]",
            base: "w-[273px]",
            input: "text-sm",
            inputWrapper: "rounded-lg h-[36px] w-[273px]",
          }}
        />

        {/* Owner Type Filter */}
        <FilterSelect
          placeholder={t('admin.onlyfans.filters.owner.placeholder')}
          options={ownerTypeOptions}
          selectedKey={filters.ownerType}
          variant="campaign"
          onSelectionChange={(value) => onFilterChange('ownerType', value)}
          showPlaceholderWithValue={true}
          startContent={
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14.1667 17.5V15.8333C14.1667 14.9493 13.8155 14.1014 13.1904 13.4763C12.5653 12.8512 11.7174 12.5 10.8333 12.5H4.16667C3.28261 12.5 2.43476 12.8512 1.80964 13.4763C1.18452 14.1014 0.833336 14.9493 0.833336 15.8333V17.5" stroke="#71717A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M7.50001 9.16667C9.34096 9.16667 10.8333 7.67428 10.8333 5.83333C10.8333 3.99238 9.34096 2.5 7.50001 2.5C5.65906 2.5 4.16667 3.99238 4.16667 5.83333C4.16667 7.67428 5.65906 9.16667 7.50001 9.16667Z" stroke="#71717A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M19.1667 17.5V15.8333C19.1662 15.0948 18.9203 14.3773 18.4681 13.7936C18.0159 13.2099 17.3841 12.793 16.6667 12.6083" stroke="#71717A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M13.3333 2.60833C14.0525 2.79192 14.686 3.20892 15.1394 3.79359C15.5929 4.37827 15.8394 5.09736 15.8394 5.8375C15.8394 6.57764 15.5929 7.29673 15.1394 7.88141C14.686 8.46608 14.0525 8.88308 13.3333 9.06667" stroke="#71717A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          }
          autoWidth={true}
        />

        {/* Status Filter */}
        <FilterSelect
          placeholder={t('admin.onlyfans.filters.status.placeholder')}
          options={statusOptions}
          selectedKey={filters.status}
          variant="campaign"
          onSelectionChange={(value) => onFilterChange('status', value)}
          startContent={
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 18.3333C14.6024 18.3333 18.3333 14.6024 18.3333 10C18.3333 5.39763 14.6024 1.66667 10 1.66667C5.39763 1.66667 1.66667 5.39763 1.66667 10C1.66667 14.6024 5.39763 18.3333 10 18.3333Z" stroke="#71717A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M10 6.66667V10" stroke="#71717A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M10 13.3333H10.0083" stroke="#71717A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          }
          autoWidth={true}
        />

        {/* Reset Filters Button */}
        {hasActiveFilters && (
          <Tooltip content={t('admin.onlyfans.filters.resetTooltip')} placement="top">
            <Button
              size="sm"
              className="h-[36px] rounded-[8px] !w-[36px] p-0 bg-[#F4F4F5] min-w-[36px]"
              onPress={onResetFilters}
            >
              <ResetFiltersIc />
            </Button>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

export default FiltersSection;
